import { useState, useEffect, useCallback } from "react";
import {
  Activity, Target, AlertTriangle, Hammer, PenLine, Users, LineChart, GraduationCap,
  MessageSquare, Coins, Compass, Lock, Sparkles, Loader2, ArrowRight, Volume2, RefreshCw, Lightbulb, X,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import CareerUnlockModal from "./CareerUnlockModal";
import InterviewPractice from "./InterviewPractice";
import { MODELS } from "./NewApplication";

const STAGES = [
  { id: "diagnose", label: "Diagnose", q: "Why am I struggling?", icon: Activity, kind: "coach" },
  { id: "target", label: "Target", q: "What jobs should I realistically pursue?", icon: Target, kind: "coach", input: "Any target role, industry or location? (optional)" },
  { id: "gap", label: "Gap", q: "What is stopping me getting those jobs?", icon: AlertTriangle, kind: "coach", input: "Which roles are you targeting?" },
  { id: "build", label: "Build", q: "Create credible evidence of missing capabilities", icon: Hammer, kind: "coach", input: "Which capability do you want to prove?" },
  { id: "apply", label: "Apply", q: "Create an exceptional application", icon: PenLine, kind: "link", view: "new" },
  { id: "connect", label: "Connect", q: "Approach recruiters & hiring managers intelligently", icon: Users, kind: "coach", input: "Which role & company (and person, if known)?" },
  { id: "track", label: "Track", q: "Measure what is happening", icon: LineChart, kind: "link", view: "tracker" },
  { id: "learn", label: "Learn", q: "Why applications succeed or fail", icon: GraduationCap, kind: "coach" },
  { id: "interview", label: "Interview", q: "Prepare specifically for that employer", icon: MessageSquare, kind: "coach", input: "Which employer & role are you interviewing for?" },
  { id: "offer", label: "Offer", q: "Help negotiate", icon: Coins, kind: "coach", input: "Share the offer details (base, role, location…)" },
  { id: "transition", label: "Transition", q: "Where my career can go next", icon: Compass, kind: "coach" },
];

export default function CareerJourney({ onNavigate, applications = [] }) {
  const [access, setAccess] = useState(false);
  const [plan, setPlan] = useState(null);
  const [manageBilling, setManageBilling] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [outputs, setOutputs] = useState({});
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [input, setInput] = useState("");
  const [coachModel, setCoachModel] = useState(() => localStorage.getItem("coach_model") || "gpt");
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [freeUntil, setFreeUntil] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/career/status");
      setAccess(!!data.access);
      setPlan(data.plan);
      setManageBilling(!!data.manage_billing);
      setFreeUntil(data.free_until || null);
      if (data.access) {
        const { data: outs } = await api.get("/career/outputs");
        setOutputs(outs || {});
      }
    } catch { /* treat as locked */ } finally { setLoaded(true); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Journey progress across all 11 stages.
  const isDone = (s) => {
    if (s.kind === "coach") return !!outputs[s.id];
    if (s.id === "apply") return applications.length > 0;
    if (s.id === "track") return applications.some((a) => a.status && a.status !== "tailored");
    return false;
  };
  const completed = access ? STAGES.filter(isDone).length : 0;
  const pct = Math.round((completed / STAGES.length) * 100);

  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const suggestNext = () => {
    if (!access) return null;
    const total = applications.length;
    const avg = total ? Math.round(applications.reduce((a, x) => a + (x.ats_score || 0), 0) / total) : 0;
    const interviews = applications.filter((a) => ["interview", "offer"].includes(a.status)).length;
    const doneIds = STAGES.filter(isDone).map((s) => s.id);
    let id;
    if (total === 0) id = "apply";
    else if (avg && avg < 70) id = "diagnose";
    else if (total >= 5 && interviews === 0) id = "learn";
    else id = (STAGES.find((s) => s.kind === "coach" && !doneIds.includes(s.id)) || {}).id;
    if (!id) return null;
    const stage = STAGES.find((s) => s.id === id);
    const msgs = {
      apply: "You haven't tailored an application yet — let's create your first exceptional one.",
      diagnose: `Your average ATS match is ${avg}%. Let's diagnose what's holding it back.`,
      learn: "Lots of applications but no interviews yet — let's learn why and adjust.",
    };
    return { stage, msg: msgs[id] || `Ready for the next step? Try ${stage.label}.` };
  };
  const nudge = suggestNext();

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/career/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      setPortalLoading(false);
      toast.error(e?.response?.data?.detail || "Couldn't open billing portal");
    }
  };

  const openStage = (stage) => {
    if (stage.kind === "link") { onNavigate?.(stage.view); return; }
    if (!access) { setUnlockOpen(true); return; }
    setActive(stage);
    const saved = outputs[stage.id];
    setResult(saved?.result || null);
    setInput(saved?.input || "");
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/career/coach", { stage: active.id, input, model: coachModel });
      setResult(data.result);
      setOutputs((o) => ({ ...o, [active.id]: { result: data.result, input } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Coaching failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const readAloud = async () => {
    if (!result) return;
    const text = result.summary + ". " + result.blocks.flatMap((b) => [b.title, ...b.points]).join(". ");
    try {
      setSpeaking(true);
      const res = await api.post("/voice/tts", { text: text.slice(0, 3800), voice: "nova" }, { responseType: "blob" });
      const audio = new Audio(URL.createObjectURL(res.data));
      audio.onended = () => setSpeaking(false);
      audio.onerror = () => setSpeaking(false);
      await audio.play();
    } catch { setSpeaking(false); toast.error("Couldn't play audio"); }
  };

  return (
    <div className="af-fade-up" data-testid="career-journey-view">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Career Journey</h1>
        <span className={`flex items-center gap-1.5 text-xs font-mono2 uppercase tracking-widest px-2 py-0.5 rounded-full border ${
 access ? "text-kesh-text bg-kesh-soft border-kesh/25" : "text-ink bg-subtle border-line"
 }`}>
          {access ? <><Sparkles className="w-3 h-3" /> Unlocked</> : <><Lock className="w-3 h-3" /> Premium</>}
        </span>
      </div>
      <p className="text-body mb-6">Not just a résumé — your AI coach for the whole journey, from working out why you're stuck to negotiating the offer.</p>

      {access && loaded && (
        <div className="rounded-xl border border-line bg-surface p-5 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="career-progress">
          <div className="flex items-center gap-4">
            <div className="relative w-16 h-16">
              <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
                <circle cx="18" cy="18" r="15.9155" fill="none" stroke="rgb(var(--gi-line))" strokeWidth="3" />
                <circle cx="18" cy="18" r="15.9155" fill="none" stroke="rgb(var(--gi-kesh))" strokeWidth="3"
                        strokeDasharray={`${pct}, 100`} strokeLinecap="round"
                        style={{ transition: "stroke-dasharray 0.6s ease" }} />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center font-display font-bold text-sm" data-testid="career-progress-pct">{pct}%</span>
            </div>
            <div>
              <div className="font-semibold">Your journey progress</div>
              <div className="text-sm text-body">{completed} of {STAGES.length} stages worked through</div>
              {freeUntil && <div className="text-xs font-medium text-kesh-text mt-0.5" data-testid="career-free-until">Free from inviting friends until {new Date(freeUntil).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" })}</div>}
            </div>
          </div>
          {manageBilling && (
            <button
              data-testid="manage-billing-button"
              onClick={openPortal}
              disabled={portalLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-line text-sm text-ink hover:bg-subtle transition-colors duration-200 disabled:opacity-60"
            >
              {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
              {plan === "monthly" ? "Manage subscription" : "Manage billing"}
            </button>
          )}
        </div>
      )}

      {access && loaded && nudge && !nudgeDismissed && (
        <div className="rounded-xl border border-brand/30 bg-brand-soft p-4 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="stage-nudge">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-brand-soft border border-brand/30 flex items-center justify-center shrink-0">
              <Lightbulb className="w-5 h-5 text-brand" />
            </div>
            <div>
              <div className="text-xs font-mono2 uppercase tracking-widest text-brand mb-0.5">Suggested next step</div>
              <div className="text-sm text-ink">{nudge.msg}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="nudge-open-button"
              onClick={() => openStage(nudge.stage)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand text-onbrand text-sm font-semibold hover:bg-brand-hover transition-colors"
            >
              Open {nudge.stage.label} <ArrowRight className="w-4 h-4" />
            </button>
            <button data-testid="nudge-dismiss-button" onClick={() => setNudgeDismissed(true)} className="p-2 rounded-lg text-faint hover:text-ink hover:bg-subtle transition-colors" title="Dismiss">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {!access && loaded && (
        <div className="rounded-xl border border-line bg-surface p-5 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="career-locked-banner">
          <div className="text-sm text-body">Preview every stage below. Unlock to get personalised coaching on each.</div>
          <button
            data-testid="career-unlock-button"
            onClick={() => setUnlockOpen(true)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand text-onbrand text-sm font-semibold hover:bg-brand-hover transition-colors"
          >
            <Sparkles className="w-4 h-4" /> Unlock Career Journey
          </button>
        </div>
      )}

      {!loaded ? (
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-brand animate-spin" /></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {STAGES.map((s, i) => {
            const done = access && outputs[s.id];
            return (
              <button
                key={s.id}
                data-testid={`career-stage-${s.id}`}
                onClick={() => openStage(s)}
                className="text-left rounded-xl border border-line bg-surface p-5 hover:bg-subtle hover:border-brand/40 transition-colors duration-200 relative group"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-soft border border-brand/30 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-brand" />
                  </div>
                  <span className="font-mono2 text-xs text-faint">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="font-display font-semibold text-lg">{s.label}</h3>
                  {s.kind === "link" && <ArrowRight className="w-4 h-4 text-faint group-hover:text-brand transition-colors" />}
                  {done && <span className="w-1.5 h-1.5 rounded-full bg-brand" title="Coached" />}
                </div>
                <p className="text-sm text-body mt-1 leading-relaxed">{s.q}</p>
                {!access && s.kind === "coach" && (
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-faint font-semibold uppercase tracking-wide">
                    <Lock className="w-3 h-3" /> Unlock
                  </div>
                )}
                {s.kind === "link" && (
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-brand font-mono2 uppercase tracking-widest">
                    Open tool
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      <CareerUnlockModal open={unlockOpen} onOpenChange={(v) => { setUnlockOpen(v); if (!v) load(); }} />

      <Dialog open={!!active} onOpenChange={(v) => { if (!v) setActive(null); }}>
        <DialogContent className="bg-surface border-line text-ink sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="career-coach-dialog">
          {active && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 text-brand">
                  <active.icon className="w-5 h-5" />
                  <DialogTitle className="font-display text-xl">{active.label}</DialogTitle>
                </div>
                <DialogDescription className="text-body text-sm">{active.q}</DialogDescription>
              </DialogHeader>

              {active.input && (
                <textarea
                  data-testid="career-coach-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  rows={2}
                  placeholder={active.input}
                  className="w-full bg-canvas border border-line-strong rounded-lg px-4 py-2.5 text-sm text-ink placeholder:text-faint focus:outline-none focus:border-brand transition-colors resize-y"
                />
              )}

              <div className="flex flex-wrap items-center gap-2" data-testid="career-coach-model-picker">
                <span className="text-xs text-faint mr-1">AI coach:</span>
                {MODELS.map((m) => (
                  <button
                    key={m.id}
                    data-testid={`coach-model-${m.id}`}
                    onClick={() => { setCoachModel(m.id); localStorage.setItem("coach_model", m.id); }}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${coachModel === m.id ? "border-brand bg-brand-soft text-brand" : "border-line text-body hover:border-brand/40"}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <button
                  data-testid="career-coach-generate"
                  onClick={generate}
                  disabled={generating}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand text-onbrand text-sm font-semibold hover:bg-brand-hover transition-colors disabled:opacity-60"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : result ? <RefreshCw className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                  {result ? "Regenerate" : "Get coaching"}
                </button>
                {result && (
                  <button
                    data-testid="career-coach-read"
                    onClick={readAloud}
                    disabled={speaking}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-line text-ink text-sm hover:bg-subtle transition-colors disabled:opacity-60"
                  >
                    {speaking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />} Read aloud
                  </button>
                )}
              </div>

              {generating && !result && (
                <div className="py-8 text-center text-body text-sm">Coaching in progress… this can take a few seconds.</div>
              )}

              {result && (
                <div className="space-y-4" data-testid="career-coach-result">
                  <p className="text-sm text-ink leading-relaxed border-l-2 border-brand/40 pl-3">{result.summary}</p>
                  {result.blocks.map((b, i) => (
                    <div key={i} className="rounded-lg border border-line bg-canvas p-4">
                      {b.title && <div className="font-semibold text-sm mb-2">{b.title}</div>}
                      <ul className="space-y-1.5">
                        {b.points.map((p, j) => (
                          <li key={j} className="flex gap-2 text-sm text-body leading-relaxed">
                            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand shrink-0" />
                            <span>{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  {result.next_steps?.length > 0 && (
                    <div className="rounded-lg border border-brand/30 bg-brand-soft p-4">
                      <div className="font-semibold text-sm mb-2 text-brand">Do this next</div>
                      <ul className="space-y-1.5">
                        {result.next_steps.map((s, i) => (
                          <li key={i} className="flex gap-2 text-sm text-ink"><ArrowRight className="w-4 h-4 text-brand shrink-0 mt-0.5" /> {s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {active.id === "interview" && (result || input) && (
                <InterviewPractice
                  context={input}
                  result={result}
                  suggested={result?.blocks?.[0]?.points?.[0]?.replace(/\s*[—–-]\s*(tip|how to answer).*/i, "").trim() || ""}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
